import { doc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { callableMissing } from '../utils/liveRoomUi';
import { confirmLiveDone } from './liveV2';
import { playerDonePayload } from '../utils/callEnd';

/**
 * The player's rating after a lesson: stars for the coach's rating, with an
 * optional review the coach sees (decision 106), in one call. The server
 * rates a lesson once, and the rating closes it (decision 105).
 *
 * Both go through rateLiveLesson, which UI/UX is adding. Until it lands
 * (a call to it answers not-found, unimplemented or internal):
 * - the stars fall back to where they went before, the pair's room document
 *   (ratings.{uid}), so a rating is never lost;
 * - the note is kept on this device and sent the next time the rating
 *   screen opens with the callable there. It is never written where the
 *   coach can read it, so it does not go to the room document.
 */
interface RateInput { bookingId: string; stars: number; note?: string; requestId?: string }

const rateLiveLesson = httpsCallable<RateInput, { ok: true }>(functions, 'rateLiveLesson');
const PENDING_KEY = 'db_lesson_feedback_pending';

/**
 * The server takes a rating once the lesson has finished (both Done, or the
 * call closed, decision 56). Rated on Done before then, it waits on this
 * device and goes the next time the rating screen opens (flushLessonNotes).
 */
export function notRateableYet(err: unknown): boolean {
  return (err as { code?: string } | null)?.code === 'functions/failed-precondition';
}

export async function rateStars(p: { bookingId: string; roomId: string; uid: string; stars: number; requestId?: string }): Promise<'sent' | 'kept'> {
  try {
    await rateLiveLesson({ bookingId: p.bookingId, stars: p.stars, ...(p.requestId && { requestId: p.requestId }) });
    return 'sent';
  } catch (err) {
    if (notRateableYet(err)) { keep({ bookingId: p.bookingId, stars: p.stars }); return 'kept'; }
    if (!callableMissing(err)) throw err;
    try {
      await updateDoc(doc(db, 'webrtc_rooms', p.roomId), {
        [`ratings.${p.uid}`]: { stars: p.stars, bookingId: p.bookingId, at: new Date().toISOString() },
      });
    } catch {
      // The room is closed to writes once the coach has ended it: the stars wait
      // on this device with any note, for the callable.
      keep({ bookingId: p.bookingId, stars: p.stars });
    }
    return 'kept';
  }
}

/** Keeps a rating on this device, merged with what is already kept for the lesson. */
function keep(r: RateInput): void {
  const pending = readPending();
  const was = pending.find((x) => x.bookingId === r.bookingId);
  writePending([...pending.filter((x) => x.bookingId !== r.bookingId), { ...was, ...r, note: r.note ?? was?.note }]);
}

/** Ratings kept on this device for the callable, for "rated already" (utils/lessonRated). */
export function keptRatings(): RateInput[] { return readPending(); }

const readPending = (): RateInput[] => {
  try { return JSON.parse(localStorage.getItem(PENDING_KEY) ?? '[]') as RateInput[]; } catch { return []; }
};
const writePending = (xs: RateInput[]) => {
  try { if (xs.length) localStorage.setItem(PENDING_KEY, JSON.stringify(xs)); else localStorage.removeItem(PENDING_KEY); } catch { /* this page only */ }
};

/** Sends the note; until the callable lands, keeps it on this device. Resolves either way. */
export async function sendLessonNote(p: RateInput): Promise<'sent' | 'kept'> {
  try {
    await rateLiveLesson(p);
    return 'sent';
  } catch (err) {
    if (!callableMissing(err) && !notRateableYet(err)) throw err;
    keep(p);
    return 'kept';
  }
}

/** The server rates a lesson once: this lesson was rated already (here, or on its page). */
export function alreadyRated(err: unknown): boolean {
  return (err as { code?: string } | null)?.code === 'functions/already-exists';
}

/** Notes kept while the callable was missing, sent now if it is there; one already rated is done with. */
export async function flushLessonNotes(): Promise<void> {
  const pending = readPending();
  if (!pending.length) return;
  const left: RateInput[] = [];
  for (const p of pending) {
    try { await rateLiveLesson(p); } catch (err) { if (!alreadyRated(err)) left.push(p); }
  }
  writePending(left);
}

/**
 * The player's one Done after the call (decision 105): their confirmation
 * with the stars and the review, in one call, which finishes the lesson at
 * once. Sent without waiting (the screen has gone to Lessons); if it does
 * not go, the stars and the review wait on this device for rateLiveLesson,
 * as a rating always has (flushLessonNotes).
 */
export async function doneWithRating(bookingId: string, stars: number, review: string): Promise<'sent' | 'kept'> {
  const payload = playerDonePayload(bookingId, stars, review);
  try {
    await confirmLiveDone(payload);
    return 'sent';
  } catch {
    if (payload.rating) keep({ bookingId, stars: payload.rating.stars, ...(payload.rating.text ? { note: payload.rating.text } : {}) });
    return 'kept';
  }
}
